// BlogCategoryPage.jsx

import React, {
  useMemo,
  useState
} from "react";

import {
  Link,
  useParams
} from "react-router-dom";

import MarketingPageShell from "../components/layout/MarketingPageShell";

import SeoHead from "../components/seo/SeoHead";

import Breadcrumbs from "../components/blog/Breadcrumbs";

import StructuredData, {
  buildBreadcrumbSchema,
  buildWebPageSchema,
  buildOrganizationSchema
} from "../components/seo/StructuredData";

import useThemeMode from "../hooks/useThemeMode";

import {
  getMarketingTheme
} from "../utils/marketingTheme";

import blogPosts from "../data/blogPosts";

function BlogCategoryPage() {
  const { slug } =
    useParams();

  const { isDark } =
    useThemeMode();

  const colors = useMemo(
    () =>
      getMarketingTheme(
        isDark
      ),
    [isDark]
  );

  const [
    searchQuery,
    setSearchQuery
  ] = useState("");

  const categoryMap = {
    hooks: {
      title: "Hooks",

      description:
        "Explore hook writing guides, viral hook examples, retention strategies, and creator attention frameworks."
    },

    "creator-workflow": {
      title:
        "Creator Workflow",

      description:
        "Explore creator workflow systems for hooks, captions, scripts, titles, content packaging, and publishing consistency."
    },

    "ai-tools": {
      title:
        "AI Tools",

      description:
        "Discover AI tools creators use for hooks, captions, scripts, workflow optimization, and faster content creation."
    },

    retention: {
      title:
        "Retention",

      description:
        "Learn retention strategies creators use to improve watch time, engagement, pacing, and short-form performance."
    },

    shorts: {
      title:
        "Shorts",

      description:
        "Explore YouTube Shorts strategies for hooks, retention, watch time, storytelling, and creator growth."
    },

    reels: {
      title:
        "Reels",

      description:
        "Discover Instagram Reels strategies creators use to improve attention, retention, engagement, and performance."
    },

    tiktok: {
      title:
        "TikTok",

      description:
        "Explore TikTok hook formulas, watch time strategies, viral structures, and short-form content systems."
    }
  };

  const currentCategory =
    categoryMap[slug] || {
      title:
        "Blog Category",

      description:
        "Explore creator-focused content and workflow guides."
    };

  const categoryPosts =
    blogPosts.filter(
      (post) =>
        post.category ===
        slug
    );

  const featuredPosts =
    categoryPosts.filter(
      (post) =>
        post.featured
    );

  const filteredPosts =
    categoryPosts.filter(
      (post) => {
        const search =
          searchQuery.toLowerCase();

        const matchesSearch =
          post.title
            .toLowerCase()
            .includes(
              search
            ) ||

          post.description
            .toLowerCase()
            .includes(
              search
            ) ||

          (
            post.keywords ||
            []
          ).some(
            (keyword) =>
              keyword
                .toLowerCase()
                .includes(
                  search
                )
          );

        return matchesSearch;
      }
    );

  const breadcrumbItems =
    [
      {
        label:
          "Home",

        to: "/"
      },

      {
        label:
          "Blog",

        to: "/blog"
      },

      {
        label:
          currentCategory.title,

        to: `/blog/category/${slug}`
      }
    ];

  return (
    <MarketingPageShell>
      <SeoHead
        title={`${currentCategory.title} | Hookora Blog`}
        description={
          currentCategory.description
        }
        path={`/blog/category/${slug}`}
      />

      <StructuredData
        id="blog-category-org-schema"
        data={buildOrganizationSchema()}
      />

      <StructuredData
        id="blog-category-page-schema"
        data={buildWebPageSchema(
          {
            name:
              currentCategory.title,

            description:
              currentCategory.description,

            url: `https://hookora.app/blog/category/${slug}`,

            type:
              "CollectionPage"
          }
        )}
      />

      <StructuredData
        id="blog-category-breadcrumb-schema"
        data={buildBreadcrumbSchema(
          breadcrumbItems
        )}
      />

      <div
        style={{
          maxWidth:
            "1200px",

          margin:
            "0 auto",

          padding:
            "34px 20px 0"
        }}
      >
        <Breadcrumbs
          items={
            breadcrumbItems
          }
        />
      </div>

      {/* HERO */}

      <section
        style={{
          padding:
            "44px 20px 34px",

          background:
            colors.heroBg
        }}
      >
        <div
          style={{
            maxWidth:
              "1100px",

            margin:
              "0 auto"
          }}
        >
          <div
            style={{
              display:
                "inline-flex",

              padding:
                "8px 14px",

              borderRadius:
                "999px",

              background:
                colors.badgeBg,

              color:
                colors.badgeText,

              fontWeight:
                "700",

              fontSize:
                "12px",

              marginBottom:
                "18px",

              border: isDark
                ? `1px solid ${colors.border}`
                : "none"
            }}
          >
            Blog Category
          </div>

          <h1
            style={{
              fontSize:
                "clamp(2.4rem, 5vw, 4rem)",

              lineHeight:
                "1.05",

              margin:
                "0 0 16px",

              color:
                colors.text
            }}
          >
            {
              currentCategory.title
            }
          </h1>

          <p
            style={{
              maxWidth:
                "760px",

              lineHeight:
                "1.8",

              fontSize:
                "17px",

              color:
                colors.textSoft,

              margin:
                "0 0 28px"
            }}
          >
            {
              currentCategory.description
            }
          </p>

          {/* SEARCH */}

          <div
            style={{
              maxWidth:
                "760px",

              margin:
                "0 0 26px"
            }}
          >
            <input
              type="text"
              placeholder={`Search ${currentCategory.title} articles...`}
              value={
                searchQuery
              }
              onChange={(
                e
              ) =>
                setSearchQuery(
                  e.target
                    .value
                )
              }
              style={{
                width:
                  "100%",

                padding:
                  "16px 18px",

                borderRadius:
                  "16px",

                border: `1px solid ${colors.borderStrong}`,

                background:
                  colors.panelBg,

                color:
                  colors.text,

                fontSize:
                  "15px",

                outline:
                  "none"
              }}
            />
          </div>

          {/* CATEGORY LINKS */}

          <div
            style={{
              display:
                "flex",

              flexWrap:
                "wrap",

              gap: "12px"
            }}
          >
            {Object.entries(
              categoryMap
            ).map(
              ([
                categorySlug,
                category
              ]) => {
                const active =
                  categorySlug ===
                  slug;

                return (
                  <Link
                    key={
                      categorySlug
                    }
                    to={`/blog/category/${categorySlug}`}
                    style={{
                      textDecoration:
                        "none",

                      padding:
                        "12px 16px",

                      borderRadius:
                        "999px",

                      background:
                        active
                          ? "linear-gradient(135deg, #2563eb, #7c3aed)"
                          : colors.panelBg,

                      color:
                        active
                          ? "#fff"
                          : colors.text,

                      border:
                        active
                          ? "none"
                          : `1px solid ${colors.borderStrong}`,

                      fontSize:
                        "13px",

                      fontWeight:
                        "700",

                      transition:
                        "all 0.2s ease"
                    }}
                  >
                    {
                      category.title
                    }
                  </Link>
                );
              }
            )}
          </div>
        </div>
      </section>

      {/* FEATURED */}

      {featuredPosts.length >
        0 &&
        searchQuery ===
          "" && (
          <section
            style={{
              padding:
                "10px 20px 30px",

              background:
                colors.sectionBg
            }}
          >
            <div
              style={{
                maxWidth:
                  "1200px",

                margin:
                  "0 auto"
              }}
            >
              <div
                style={{
                  display:
                    "flex",

                  justifyContent:
                    "space-between",

                  alignItems:
                    "center",

                  flexWrap:
                    "wrap",

                  gap: "12px",

                  marginBottom:
                    "24px"
                }}
              >
                <h2
                  style={{
                    margin: 0,

                    color:
                      colors.text,

                    fontSize:
                      "28px"
                  }}
                >
                  Featured{" "}
                  {
                    currentCategory.title
                  }
                </h2>

                <div
                  style={{
                    color:
                      colors.textMuted,

                    fontSize:
                      "14px",

                    fontWeight:
                      "600"
                  }}
                >
                  Top articles
                  in this
                  category
                </div>
              </div>

              <div
                style={{
                  display:
                    "grid",

                  gridTemplateColumns:
                    "repeat(auto-fit, minmax(320px, 1fr))",

                  gap: "18px"
                }}
              >
                {featuredPosts.map(
                  (
                    post
                  ) => (
                    <article
                      key={
                        post.slug
                      }
                      style={{
                        background:
                          colors.cardBg,

                        border: `1px solid ${colors.border}`,

                        borderRadius:
                          "28px",

                        padding:
                          "28px",

                        boxShadow:
                          colors.shadow
                      }}
                    >
                      <div
                        style={{
                          display:
                            "flex",

                          justifyContent:
                            "space-between",

                          alignItems:
                            "center",

                          flexWrap:
                            "wrap",

                          gap: "12px",

                          marginBottom:
                            "18px"
                        }}
                      >
                        <div
                          style={{
                            display:
                              "inline-flex",

                            padding:
                              "7px 12px",

                            borderRadius:
                              "999px",

                            background:
                              "linear-gradient(135deg, #2563eb, #7c3aed)",

                            color:
                              "#fff",

                            fontWeight:
                              "700",

                            fontSize:
                              "12px"
                          }}
                        >
                          Featured
                        </div>

                        <div
                          style={{
                            fontSize:
                              "12px",

                            fontWeight:
                              "700",

                            color:
                              colors.textMuted
                          }}
                        >
                          {
                            post.readTime
                          }
                        </div>
                      </div>

                      <h2
                        style={{
                          margin:
                            "0 0 14px",

                          color:
                            colors.text,

                          fontSize:
                            "28px",

                          lineHeight:
                            "1.2"
                        }}
                      >
                        {
                          post.title
                        }
                      </h2>

                      <p
                        style={{
                          margin:
                            "0 0 22px",

                          color:
                            colors.textMuted,

                          lineHeight:
                            "1.8",

                          fontSize:
                            "15px"
                        }}
                      >
                        {
                          post.description
                        }
                      </p>

                      <Link
                        to={
                          post.slug
                        }
                        style={{
                          display:
                            "inline-flex",

                          textDecoration:
                            "none",

                          background:
                            colors.panelBg,

                          color:
                            colors.text,

                          padding:
                            "12px 16px",

                          borderRadius:
                            "12px",

                          border: `1px solid ${colors.borderStrong}`,

                          fontWeight:
                            "800"
                        }}
                      >
                        Read Guide
                      </Link>
                    </article>
                  )
                )}
              </div>
            </div>
          </section>
        )}

      {/* POSTS */}

      <section
        style={{
          padding:
            "10px 20px 84px",

          background:
            colors.sectionBg
        }}
      >
        <div
          style={{
            maxWidth:
              "1200px",

            margin:
              "0 auto"
          }}
        >
          <div
            style={{
              display:
                "flex",

              justifyContent:
                "space-between",

              alignItems:
                "center",

              flexWrap:
                "wrap",

              gap: "12px",

              marginBottom:
                "22px"
            }}
          >
            <div
              style={{
                color:
                  colors.textMuted,

                fontWeight:
                  "600",

                fontSize:
                  "14px"
              }}
            >
              {
                filteredPosts.length
              }{" "}
              articles
            </div>

            <Link
              to="/blog"
              style={{
                textDecoration:
                  "none",

                color:
                  "#7c3aed",

                fontWeight:
                  "700",

                fontSize:
                  "14px"
              }}
            >
              ← Back to Blog
            </Link>
          </div>

          <div
            style={{
              display:
                "grid",

              gridTemplateColumns:
                "repeat(auto-fit, minmax(280px, 1fr))",

              gap: "18px"
            }}
          >
            {filteredPosts.map(
              (post) => (
                <article
                  key={
                    post.slug
                  }
                  style={{
                    background:
                      colors.cardBg,

                    border: `1px solid ${colors.border}`,

                    borderRadius:
                      "24px",

                    padding:
                      "24px",

                    boxShadow:
                      colors.shadow
                  }}
                >
                  <div
                    style={{
                      display:
                        "flex",

                      justifyContent:
                        "space-between",

                      alignItems:
                        "center",

                      flexWrap:
                        "wrap",

                      gap: "10px",

                      marginBottom:
                        "14px"
                    }}
                  >
                    <div
                      style={{
                        display:
                          "inline-flex",

                        padding:
                          "7px 12px",

                        borderRadius:
                          "999px",

                        background:
                          isDark
                            ? "rgba(109,40,217,0.18)"
                            : "#f5f3ff",

                        color:
                          isDark
                            ? "#d8b4fe"
                            : "#6d28d9",

                        fontWeight:
                          "700",

                        fontSize:
                          "12px",

                        border:
                          isDark
                            ? `1px solid ${colors.border}`
                            : "none"
                      }}
                    >
                      {
                        post.badge
                      }
                    </div>

                    <div
                      style={{
                        fontSize:
                          "12px",

                        fontWeight:
                          "700",

                        color:
                          colors.textMuted
                      }}
                    >
                      #
                      {
                        post.categoryLabel
                      }
                    </div>
                  </div>

                  <h2
                    style={{
                      margin:
                        "0 0 14px",

                      color:
                        colors.text,

                      fontSize:
                        "24px",

                      lineHeight:
                        "1.3"
                    }}
                  >
                    {
                      post.title
                    }
                  </h2>

                  <p
                    style={{
                      margin:
                        "0 0 20px",

                      color:
                        colors.textMuted,

                      lineHeight:
                        "1.8"
                    }}
                  >
                    {
                      post.description
                    }
                  </p>

                  {/* META */}

                  <div
                    style={{
                      display:
                        "flex",

                      gap: "12px",

                      flexWrap:
                        "wrap",

                      alignItems:
                        "center",

                      marginBottom:
                        "18px",

                      color:
                        colors.textMuted,

                      fontSize:
                        "12px",

                      fontWeight:
                        "600"
                    }}
                  >
                    <span>
                      {
                        post.readTime
                      }
                    </span>

                    <span>
                      •
                    </span>

                    <span>
                      July 2026
                    </span>
                  </div>

                  {/* KEYWORDS */}

                  <div
                    style={{
                      display:
                        "flex",

                      flexWrap:
                        "wrap",

                      gap: "8px",

                      marginBottom:
                        "22px"
                    }}
                  >
                    {(
                      post.keywords ||
                      []
                    )
                      .slice(
                        0,
                        3
                      )
                      .map(
                        (
                          keyword
                        ) => (
                          <span
                            key={
                              keyword
                            }
                            style={{
                              padding:
                                "6px 10px",

                              borderRadius:
                                "999px",

                              fontSize:
                                "11px",

                              fontWeight:
                                "600",

                              background:
                                colors.panelBg,

                              color:
                                colors.textMuted,

                              border: `1px solid ${colors.border}`
                            }}
                          >
                            {
                              keyword
                            }
                          </span>
                        )
                      )}
                  </div>

                  <Link
                    to={
                      post.slug
                    }
                    style={{
                      display:
                        "inline-flex",

                      textDecoration:
                        "none",

                      background:
                        colors.panelBg,

                      color:
                        colors.text,

                      padding:
                        "12px 16px",

                      borderRadius:
                        "12px",

                      border: `1px solid ${colors.borderStrong}`,

                      fontWeight:
                        "800"
                    }}
                  >
                    Read Guide
                  </Link>
                </article>
              )
            )}
          </div>

          {filteredPosts.length ===
            0 && (
            <div
              style={{
                padding:
                  "80px 20px",

                textAlign:
                  "center"
              }}
            >
              <h3
                style={{
                  color:
                    colors.text,

                  marginBottom:
                    "12px"
                }}
              >
                No articles found
              </h3>

              <p
                style={{
                  color:
                    colors.textMuted,

                  maxWidth:
                    "520px",

                  margin:
                    "0 auto",

                  lineHeight:
                    "1.8"
                }}
              >
                Try changing
                your search
                keywords or
                explore another
                blog category.
              </p>
            </div>
          )}
        </div>
      </section>
    </MarketingPageShell>
  );
}

export default BlogCategoryPage;